// samplingEventForm/jsaForms/actions.js
import { autosaveSamplingEvent } from "../../../../api/SamplingEventsApi";
import {
  isSamplingEventSocketConnected,
  emitSamplingEventPatch,
} from "../../../../services/samplingEventSocketService";
import { applyJsaBroadcastPatch } from "../../jsaForm/jsaFormActions";

export const makeJsaFormsActions = (api) => {
  const getEvent = () => api.getCurrentEvent();
  const bump = (patch) => api.bump(patch);

  // UI helpers
  const setActiveJsaId = (jsaId) => api.setActiveJsaId(jsaId);
  const setJsaUi = (jsaId, patchOrFn) => api.setJsaUiById(jsaId, patchOrFn);
  const setJsaUiCommon = (patchOrFn) => api.setJsaUiCommon(patchOrFn);

  const openCopyModal = () => setJsaUiCommon({ isCopyModalOpen: true });
  const closeCopyModal = () => setJsaUiCommon({ isCopyModalOpen: false });
  const openDeleteModal = () => setJsaUiCommon({ isDeleteModalOpen: true });
  const closeDeleteModal = () => setJsaUiCommon({ isDeleteModalOpen: false });

  const openSignatureModal = (jsaId, signeeId) =>
    setJsaUi(jsaId, { signatureModal: { isOpen: true, signeeId } });
  const closeSignatureModal = (jsaId) =>
    setJsaUi(jsaId, { signatureModal: { isOpen: false, signeeId: null } });

  // Patch helper - sends raw patch to sampling event service
  const sendPatch = async (patch) => {
    const event = getEvent();
    if (!event) return false;
    const eventId = event.id;
    if (isSamplingEventSocketConnected()) {
      const ok = await emitSamplingEventPatch(eventId, patch);
      if (ok) return true;
    }
    try {
      const res = await autosaveSamplingEvent(eventId, patch);
      return res && res.status === 200;
    } catch {
      return false;
    }
  };

  // Helper to wrap JSA patches for routing through Sampling Event service
  // JSA field patches need to be wrapped in { patch_type: "jsa", jsa_data: {...} }
  const emitJsaPatch = async (patch) => {
    const event = getEvent();
    if (!event || !patch) return false;

    // Add del_id (sampling event uses same column in DB for JSA linking)
    const patchWithParentId = { ...patch, del_id: event.id };

    // Wrap in jsa envelope for backend routing
    const wrappedPatch = {
      patch_type: "jsa",
      jsa_data: patchWithParentId,
    };

    return sendPatch(wrappedPatch);
  };

  // Data helpers (Basic Info, RAC, Hazards, Control Measures, Signatures)
  const updateBasicInfoField = async (jsaId, fieldSnakeCase, value) => {
    // Extract scalar value from object fields (supervisor, siteLocation, etc.)
    let patchValue = value;
    if (value && typeof value === "object") {
      if (fieldSnakeCase === "supervisor_id") {
        // Handle both formats: {value, label} (React-Select) or {supervisor_id, supervisor_name}
        patchValue = value.value ?? value.supervisor_id ?? "";
      } else if (fieldSnakeCase === "site_location") {
        // Extract the label (address string) from siteLocation object
        patchValue = value.label ?? "";
      } else if ("value" in value) {
        // Generic React-Select style object
        patchValue = value.value;
      }
    }

    const patch = {
      patch_type: "jsa_basic_info",
      jsa_id: jsaId,
      [fieldSnakeCase]: patchValue,
    };
    bump();
    await emitJsaPatch(patch);
  };

  const updateRacField = async (jsaId, fieldSnakeCase, value) => {
    const patch = {
      patch_type: "jsa_rac_info",
      jsa_id: jsaId,
      [fieldSnakeCase]: value,
    };
    bump();
    await emitJsaPatch(patch);
  };

  const updateHazardField = async (jsaId, fieldSnakeCase, value) => {
    const patch = {
      patch_type: "jsa_hazards_v2",
      jsa_id: jsaId,
      [fieldSnakeCase]: value,
    };
    bump();
    await emitJsaPatch(patch);
  };

  const updateControlMeasureField = async (jsaId, fieldSnakeCase, value) => {
    const patch = {
      patch_type: "jsa_control_measures_v2",
      jsa_id: jsaId,
      [fieldSnakeCase]: value,
    };
    bump();
    await emitJsaPatch(patch);
  };

  const saveSignatures = async (jsaId, signatureLogs) => {
    const logs = Array.isArray(signatureLogs) ? signatureLogs : [signatureLogs];
    for (const sig of logs) {
      const patch = {
        patch_type: "jsa_signatures",
        jsa_id: jsaId,
        action: sig.id ? "update" : "add",
        id: sig.id,
        employee_id: sig.employee_id,
        employee_sig: sig.employee_sig,
      };
      await emitJsaPatch(patch);
    }
    bump();
  };

  const deleteSignature = async (jsaId, signatureId) => {
    const patch = {
      patch_type: "jsa_signatures",
      jsa_id: jsaId,
      action: "remove",
      id: signatureId,
    };
    bump();
    await emitJsaPatch(patch);
  };

  // JSA lifecycle - these use top-level patch_type (not wrapped in jsa envelope)
  const copyJsa = async (srcJsaId, completedDate) => {
    const patch = {
      patch_type: "sampling_event_jsa_copy",
      source_jsa_id: srcJsaId,
      completed_date: completedDate, // YYYY-MM-DD format from modal
    };
    await sendPatch(patch);
  };

  const deleteJsa = async (jsaId) => {
    const patch = {
      patch_type: "sampling_event_jsa_delete",
      jsa_id: jsaId,
    };
    await sendPatch(patch);
  };

  /**
   * Handle incoming websocket broadcast for JSA-related patch_types.
   * Mutates the JsaV2 class instance directly using setter methods.
   * @param {object} patchData - { patch_type, patch, ... }
   * @param {function} applyPatchToObject - Helper to apply patch using setters
   */
  const handleBroadcast = (patchData, applyPatchToObject) => {
    const event = getEvent();
    if (!event) return;

    const { patch_type, patch } = patchData;
    const jsas = event.jsa || [];

    // Handle sampling_event_jsa_add - add new JSA (from copy operation)
    if (patch_type === "sampling_event_jsa_add") {
      if (patch?.jsa) {
        // Use the SamplingEvent model's addNewJsa method for proper state management
        event.addNewJsa(patch.jsa);
        bump();
      }
      return;
    }

    // Handle sampling_event_jsa_remove - remove JSA (from delete operation)
    // Also handle legacy "delete_jsa" format for backwards compatibility
    if (patch_type === "sampling_event_jsa_remove" || patch_type === "delete_jsa") {
      const jsaIdToRemove = patch?.jsa_id || patch?.id;
      if (jsaIdToRemove) {
        // Use the SamplingEvent model's removeJsa method for proper state management
        event.removeJsa(jsaIdToRemove);
        bump();
      }
      return;
    }

    // Handle jsa_update - update existing JSA (legacy format, kept for backwards compatibility)
    if (patch_type === "jsa_update") {
      const jsaId = patchData.id || patch?.id;
      const jsa = jsas.find((j) => j.id === jsaId);
      if (!jsa) {
        console.warn("handleBroadcast: JSA not found for id:", jsaId);
        return;
      }

      // Route to appropriate sub-object based on patch content
      if (patch?.basic_info) {
        applyPatchToObject(jsa.jsaBasicInfo, patch.basic_info, ["id"]);
      }
      if (patch?.hazard_rac_assessment) {
        applyPatchToObject(jsa.jsaRacInfo, patch.hazard_rac_assessment, ["id"]);
      }
      if (patch?.hazards) {
        applyPatchToObject(jsa.jsaHazardsV2, patch.hazards, ["id"]);
      }
      if (patch?.control_measures) {
        applyPatchToObject(jsa.jsaControlMeasuresV2, patch.control_measures, ["id"]);
      }
      if (patch?.training) {
        applyPatchToObject(jsa.jsaTrainingInfo, patch.training, ["id"]);
      }

      bump();
    }
  };

  /**
   * Handle incoming JSA field-level broadcasts from sampling_event_jsa_update event.
   * Finds the correct JSA by jsa_id and delegates to the shared broadcast handler.
   * @param {object} patchData - { patch_type, jsa_id, ...field data }
   */
  const handleJsaBroadcast = (patchData) => {
    const event = getEvent();
    if (!event) return;

    const { jsa_id } = patchData;
    const jsas = event.jsa || [];
    const jsa = jsas.find((j) => j.jsaBasicInfo?.id === jsa_id);

    if (!jsa) {
      console.warn("handleJsaBroadcast: JSA not found for jsa_id:", jsa_id);
      return;
    }

    // Delegate to the shared JSA broadcast handler
    applyJsaBroadcastPatch(jsa, patchData);

    bump();
  };

  return Object.freeze({
    // UI
    setActiveJsaId,
    setJsaUi,
    setJsaUiCommon,
    openCopyModal,
    closeCopyModal,
    openDeleteModal,
    closeDeleteModal,
    openSignatureModal,
    closeSignatureModal,
    // Data
    updateBasicInfoField,
    updateRacField,
    updateHazardField,
    updateControlMeasureField,
    saveSignatures,
    deleteSignature,
    copyJsa,
    deleteJsa,
    // Broadcast handlers
    handleBroadcast,
    handleJsaBroadcast,
  });
};
